import {RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {Sound} from '../../../../shared/scripts/Sound'
import {CA} from '../../../../shared/scripts/analytics/Analytics'
import {UIRoomCenterSettingsModeController} from './UIRoomCenterSettingsModeController'
import {UIRoomCenterSettingsSpeedController, UIRoomCenterSettingSpeedType} from '../../../../shared/scripts/ui_room/UIRoomCenterSettingsSpeedController.ts'
import {UIRoomCenterSettingsMapController} from './UIRoomCenterSettingsMapController'
import {GameModeDataController} from '@colonist/shared-types'
import {UIRoomCenterShareLinkController} from './UIRoomCenterShareLinkController'
import {uiManager} from '../../EntryHomepage'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {socket} from '../../../../shared/scripts/EntryShared'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {AnalyticEventValue, AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import {ModeSettingEnum, RoomSettingConstants} from '@colonist/game-base-types'
import {UIRoomCenterInfoMessageController} from './UIRoomCenterInfoMessageController'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {UIRoomWarningController, WarningType} from '../../../../shared/scripts/ui_html/room_warning/UIRoomWarningController'
import {UIRoomCenterSeedOrderSelectionController} from './play_order_selection/UIRoomCenterSeedOrderSelectionController'
import {createDivIn, setElementInnerHTML, setElementOnClick} from '../../../../shared/scripts/ui_utils/DomUtils'
import {TranslationObject} from '@colonist/i18n'
import {RoomCreationPhase, RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {DiscordInstance} from '../../discord/DiscordInstance'
import {ViewPopupBecomeMemberGameplay} from '../../../../shared/scripts/ui_popup/ViewPopupBecomeMemberGameplay'
import {SDKRequest} from '../../../../../shared/DiscordActivityTypes'
import {ColonistMembershipType} from '@colonist/types'
import {UIRoomSliderController} from '../../../../shared/scripts/ui_html/room/UIRoomSliderController.ts'
import {UIGameSpriteHandler} from '../../../../shared/scripts/UIGameSpriteHandler.ts'
import {UIRoomSettingsRulesController} from '../../../../shared/scripts/ui_html/room/UIRoomSettingsRulesController'
import {UIRoomCenterSettingsMaxPlayersController} from '../../../../shared/scripts/ui_room/UIRoomCenterSettingsMaxPlayersController.ts'

export class UIWRoomCenterController {
    private readonly roomCenterContainer: HTMLDivElement
    private readonly scrollContainer: HTMLDivElement
    private readonly roomStartActionsContainer: HTMLDivElement

    private readonly cardDiscardLimitSlider: UIRoomSliderController
    private readonly victoryPointsSlider: UIRoomSliderController

    private readonly checkBoxReady: HTMLInputElement
    private readonly startButton: HTMLLinkElement
    private readonly startCustomOrderSelectionButton: HTMLLinkElement
    private readonly imReadySpan: HTMLSpanElement
    private readonly exitButton: HTMLLinkElement
    private readonly becomeAMemberButton: HTMLLinkElement | null
    private readonly modeSettingsController: UIRoomCenterSettingsModeController
    private readonly mapSettingsController: UIRoomCenterSettingsMapController
    private readonly rulesController: UIRoomSettingsRulesController
    private readonly gameSpeedSettingsController: UIRoomCenterSettingsSpeedController
    private readonly botSpeedSettingsController: UIRoomCenterSettingsSpeedController
    private readonly maxPlayersSettingsController: UIRoomCenterSettingsMaxPlayersController
    private readonly shareLinkController: UIRoomCenterShareLinkController
    private readonly seedOrderSelectionController: UIRoomCenterSeedOrderSelectionController
    readonly infoMessageController: UIRoomCenterInfoMessageController

    private roomState: RoomClientState

    private readonly DEFAULT_SLIDER_STEP = 1

    constructor() {
        this.roomCenterContainer = document.getElementById('room_center_mid') as HTMLDivElement
        this.scrollContainer = document.getElementById('room_center_scroll') as HTMLDivElement
        this.roomStartActionsContainer = document.getElementById('room_start_actions') as HTMLDivElement

        const sliderContainer = createDivIn(this.scrollContainer, 'room_center_options')
        this.victoryPointsSlider = new UIRoomSliderController(sliderContainer,
            {key: 'strings:roomPage.options.vpToWin.title'},
            RoomSettingConstants.DEFAULT_VP_TO_WIN,
            RoomSettingConstants.MIN_VP_TO_WIN,
            RoomSettingConstants.DEFAULT_VP_TO_WIN,
            this.DEFAULT_SLIDER_STEP,
            (victoryPoints) => socket.roomClient.setVictoryPointsToWin(victoryPoints))

        this.cardDiscardLimitSlider = new UIRoomSliderController(sliderContainer,
            {key: 'strings:roomPage.options.cardDiscardLimit.title'},
            RoomSettingConstants.DEFAULT_CARD_DISCARD_LIMIT,
            RoomSettingConstants.MIN_CARD_DISCARD_LIMIT,
            RoomSettingConstants.MAX_CARD_DISCARD_LIMIT,
            this.DEFAULT_SLIDER_STEP,
            (cardDiscard) => socket.roomClient.setCardDiscardLimit(cardDiscard))

        this.checkBoxReady = document.getElementById('room_center_checkbox_ready') as HTMLInputElement
        this.startButton = document.getElementById('room_center_start_button') as HTMLLinkElement
        this.startCustomOrderSelectionButton = document.getElementById('room_center_start_order_selection_button') as HTMLLinkElement
        this.imReadySpan = document.getElementById('room_center_im_ready') as HTMLSpanElement
        this.exitButton = document.getElementById('room_center_exit_button') as HTMLLinkElement
        this.becomeAMemberButton = document.getElementById('room_center_become_member_button') as HTMLLinkElement | null

        this.modeSettingsController = new UIRoomCenterSettingsModeController()
        this.mapSettingsController = new UIRoomCenterSettingsMapController()

        const rulesContainer = document.getElementById('room_center_rules_settings') as HTMLDivElement
        this.rulesController = new UIRoomSettingsRulesController(rulesContainer)

        const gameSpeedContainer = document.getElementById('room_center_gamespeed_settings') as HTMLDivElement
        const botSpeedContainer = document.getElementById('room_center_botspeed_settings') as HTMLDivElement
        const maxPlayersContainer = document.getElementById('room_center_maxplayers_settings') as HTMLDivElement
        this.gameSpeedSettingsController = new UIRoomCenterSettingsSpeedController(gameSpeedContainer, UIRoomCenterSettingSpeedType.GameSpeed)
        this.botSpeedSettingsController = new UIRoomCenterSettingsSpeedController(botSpeedContainer, UIRoomCenterSettingSpeedType.BotSpeed)
        this.maxPlayersSettingsController = new UIRoomCenterSettingsMaxPlayersController(maxPlayersContainer)

        this.shareLinkController = new UIRoomCenterShareLinkController()
        this.seedOrderSelectionController = new UIRoomCenterSeedOrderSelectionController(this.scrollContainer)

        this.infoMessageController = new UIRoomCenterInfoMessageController()
    }

    update(oldRoomState: RoomClientState | undefined, newRoomState: RoomClientState) {
        this.roomState = newRoomState
        const isGuest = UIWRoomCenterController.isGuest(newRoomState)

        this.updateTitle(newRoomState)
        this.updateScrollContainerContent()
        this.updateVisibleFooterContent(newRoomState)
        for(const sessionState of newRoomState.getSessions()) {
            if(sessionState.userSessionId == socket.controller.session.id) this.checkBoxReady.checked = sessionState.isReadyToPlay
        }

        this.modeSettingsController.update(newRoomState)
        this.mapSettingsController.update(newRoomState)
        this.rulesController.update(newRoomState)
        this.gameSpeedSettingsController.update(newRoomState)
        this.botSpeedSettingsController.update(newRoomState)
        this.maxPlayersSettingsController.update(newRoomState)

        const defaultVictoryPoints = GameModeDataController.getModeDataForMode(newRoomState.gameSetting.modeSetting)?.defaultVPsToWin ?? RoomSettingConstants.DEFAULT_VP_TO_WIN
        if(oldRoomState?.victoryPointsMaxAllowed != newRoomState.victoryPointsMaxAllowed) this.victoryPointsSlider.updateSliderConfig(defaultVictoryPoints, newRoomState.victoryPointsMaxAllowed)
        this.victoryPointsSlider.setWarningThreshold(newRoomState.victoryPointsRecommendedLimit)
        this.victoryPointsSlider.setValue(newRoomState.gameSetting.victoryPointsToWin, isGuest)
        this.cardDiscardLimitSlider.setValue(newRoomState.gameSetting.cardDiscardLimit, isGuest)

        this.updateInteractabilityOfElements(newRoomState)
    }

    load(roomState: RoomClientState) {
        this.roomState = roomState

        this.shareLinkController.load(roomState)
        this.modeSettingsController.load(roomState)
        this.mapSettingsController.load(roomState)
        this.rulesController.load(roomState)
        this.gameSpeedSettingsController.load(roomState)
        this.botSpeedSettingsController.load(roomState)
        this.maxPlayersSettingsController.load(roomState)

        this.loadFooterContent(roomState)

        setElementOnClick(this.exitButton, () => {
            Sound.playSound(Sound.SoundType.Click)
            uiManager.room.leaveRoom()
        })
        if(this.becomeAMemberButton) {
            setElementOnClick(this.becomeAMemberButton, () => {
                if(UIUtilsEjsVariables.isUserOnDiscord) {
                    const checkAction = () => {
                        void DiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.PurchaseMembership, ColonistMembershipType.PremiumMonthly)
                    }
                    new ViewPopupBecomeMemberGameplay(checkAction, AnalyticEventValue.PopupMembershipIncentiveDiscordGeneral)
                }
                UIWRoomCenterController.sendBecomeAMemberButtonAnalyticEvent()
            })
        }

        this.updateInteractabilityOfElements(roomState)

        this.update(undefined, roomState)
        this.hideSettingsIfBeginnerMode(roomState.gameSetting.modeSetting)
    }

    private loadFooterContent(roomState: RoomClientState) {
        setElementOnClick(this.checkBoxReady, () => {
            UIRoomWarningController.runWithWarning(async(userAgreed?: boolean) => {
                if(userAgreed) this.checkBoxReady.checked = true
                Sound.playSound(Sound.SoundType.Click)
                await UIGameSpriteHandler.continueAfterAssetsLoad()
                socket.roomClient.setReadyToStart(this.checkBoxReady.checked)
            }, WarningType.ReadyUpVPExceededRecommendedLimit, this.roomState)
        }, true)

        setElementOnClick(this.startButton, () => UIRoomWarningController.runWithWarning(async() => {
            Sound.playSound(Sound.SoundType.Click)
            if(this.roomState != undefined) {
                if(this.roomState.isMultiplayer()) CA.sendGoogleAnalyticsEvent(CA.Category.Funnel, CA.Label.FunnelStartedMultiplayerRoom)
            }
            await UIGameSpriteHandler.continueAfterAssetsLoad()
            socket.roomClient.startGame()
        }, WarningType.StartGameVPExceededRecommendedLimit, this.roomState))

        setElementOnClick(this.startCustomOrderSelectionButton, () => {
            Sound.playSound(Sound.SoundType.Click)
            socket.roomClient.startSeedOrderSelection()
        })
    }

    private updateInteractabilityOfElements(newRoomState: RoomClientState) {
        const isGuest = UIWRoomCenterController.isGuest(newRoomState)
        const isMember = UIWRoomCenterController.isMember()
        const isPlayOrderSelectionActive = newRoomState.gameSetting.playOrderSelectionActive

        if(this.startButton.parentElement) this.startButton.parentElement.hidden = isGuest || isPlayOrderSelectionActive
        if(this.startCustomOrderSelectionButton.parentElement) this.startCustomOrderSelectionButton.parentElement.hidden = isGuest || !isPlayOrderSelectionActive
        if(this.becomeAMemberButton?.parentElement) this.becomeAMemberButton.parentElement.hidden = isMember
        if(this.imReadySpan.parentElement) this.imReadySpan.parentElement.hidden = !isGuest
        this.modeSettingsController.updateInteractabilityOfElements(isGuest)
        this.mapSettingsController.updateInteractabilityOfElements(isGuest)
        this.rulesController.updateInteractabilityOfElements(isGuest)
        this.gameSpeedSettingsController.updateInteractabilityOfElements(newRoomState)
        this.botSpeedSettingsController.updateInteractabilityOfElements(newRoomState)
        this.maxPlayersSettingsController.updateInteractabilityOfElements(newRoomState)
    }

    private static isGuest(roomState: RoomClientState): boolean {
        return socket.controller.session.id != roomState.hostSession.userSessionId
    }

    private static isMember(): boolean {
        return ClientUserState.userState?.items.hasMembership() ?? false
    }

    private static sendBecomeAMemberButtonAnalyticEvent() {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.ABTestClickAnalytics,
            floatValue: AnalyticEventValue.RoomBecomeAMemberButton,
        }
        void CA.sendAnalyticEvent(eventData)
    }

    private updateTitle(newRoomState: RoomClientState) {
        const gameSettingsTitle = document.getElementById('room_center_game_settings_title') as HTMLDivElement
        const titleShouldBeSeedOrderSelection = this.roomState.creationPhase == RoomCreationPhase.SeedOrderSelection && gameSettingsTitle != null
        const newTitle: TranslationObject = titleShouldBeSeedOrderSelection ? {key: 'strings:roomPage.orderSelection.seedOrderSelection.title'} : {key: 'strings:roomPage.gameSettingsTitle', options: {hash: newRoomState.gameSetting.id}}
        setElementInnerHTML(gameSettingsTitle, newTitle)
    }

    private updateScrollContainerContent() {
        const shouldHideDefaultScrollContainerContent = this.roomState.creationPhase == RoomCreationPhase.SeedOrderSelection
        for(const child of this.scrollContainer.children) (child as HTMLElement).hidden = shouldHideDefaultScrollContainerContent
        this.seedOrderSelectionController.setScrollContainerContentVisibility(shouldHideDefaultScrollContainerContent)
    }

    private updateVisibleFooterContent(roomState: RoomClientState) {
        const footer = document.getElementById('room_center_footer') as HTMLDivElement
        const shouldHideDefaultFooterContent = this.roomState.creationPhase == RoomCreationPhase.SeedOrderSelection && footer != null
        for(const child of footer.children) (child as HTMLElement).hidden = shouldHideDefaultFooterContent
        this.seedOrderSelectionController.setFooterContentVisibility(shouldHideDefaultFooterContent)
    }

    private hideSettingsIfBeginnerMode(modeSetting: ModeSettingEnum) {
        const settingsOverlay = document.getElementById('room-center-settings-overlay') as HTMLDivElement
        const shouldHideSettings = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        if(settingsOverlay == null) return
        if(shouldHideSettings) {
            this.scrollContainer.style.overflowY = 'hidden'
            settingsOverlay.style.display = 'flex'
            return
        }
        this.scrollContainer.style.overflowY = 'scroll'
        settingsOverlay.style.display = 'none'
    }

    updateSeedOrderSelection(newRoomState: RoomClientState, data: RoomSeedOrderSelectionData) {
        const footer = document.getElementById('room_center_footer') as HTMLDivElement
        this.seedOrderSelectionController.updateSeedOrderSelection(newRoomState, footer, data)
    }
}
